import type { FunctionComponent } from 'react';
import { useState } from 'react';
import { ChevronDown, FileText, Globe, MapPin, Users } from 'lucide-react';

import type {
  ExtractRegistryInformationAdapterRawByVendor,
  RegistryInformationVendor,
} from './utils';
import { ctw } from '@/common';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/atoms/Collapsible';
import { hasValue } from './utils';

export const OpenCorporatesRenderRaw: FunctionComponent<{
  raw: ExtractRegistryInformationAdapterRawByVendor<
    typeof RegistryInformationVendor.OPEN_CORPORATES
  >;
}> = ({ raw }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!raw?.data) {
    return null;
  }

  const data = raw.data;

  // Filter officers that have at least occupation or currentStatus
  const officersWithDetails = data.officers.filter(o =>
    [o.occupation, o.currentStatus].some(hasValue),
  );

  const hasRegistryUrl = hasValue(data.registryUrl);
  const hasSourceDetails =
    data.source && [data.source.publisher, data.source.url, data.source.retrievedAt].some(hasValue);
  const hasRegisteredAddress =
    data.registeredAddress &&
    [
      data.registeredAddress.streetAddress,
      data.registeredAddress.locality,
      data.registeredAddress.region,
      data.registeredAddress.postalCode,
      data.registeredAddress.country,
    ].some(hasValue);
  const hasOfficerDetails = officersWithDetails.length > 0;

  const hasAnyContent = [
    hasRegistryUrl,
    hasSourceDetails,
    hasRegisteredAddress,
    hasOfficerDetails,
  ].some(hasValue);

  if (!hasAnyContent) {
    return null;
  }

  return (
    <div className="mt-8">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className="flex items-center gap-1 text-sm text-blue-600 transition-colors hover:text-blue-800 hover:underline">
          {isOpen ? 'Show less' : 'Show additional information'}
          <ChevronDown className={ctw('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
        </CollapsibleTrigger>

        <CollapsibleContent
          className={ctw(
            'space-y-6',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2',
          )}
        >
          {hasRegistryUrl && (
            <div className="border-t pt-6">
              <h3 className="mb-4 flex items-center gap-2 text-base font-semibold">
                <Globe size={18} />
                Official Registry
              </h3>
              <div className="rounded-lg border bg-white p-4">
                <a
                  href={data.registryUrl ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                >
                  {data.registryUrl}
                </a>
              </div>
            </div>
          )}

          {hasSourceDetails && (
            <div className="border-t pt-6">
              <h3 className="mb-4 flex items-center gap-2 text-base font-semibold">
                <FileText size={18} />
                Source Details
              </h3>
              <div className="space-y-2 rounded-lg border bg-white p-4">
                <div className="text-sm">
                  <span className="text-slate-600">Publisher: </span>
                  <span className="text-slate-900">{data.source.publisher}</span>
                </div>
                <div className="text-sm">
                  <span className="text-slate-600">Source URL: </span>
                  <a
                    href={data.source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {data.source.url}
                  </a>
                </div>
                <div className="text-sm">
                  <span className="text-slate-600">Retrieved At: </span>
                  <span className="text-slate-900">{data.source.retrievedAt}</span>
                </div>
              </div>
            </div>
          )}

          {hasRegisteredAddress && (
            <div className="border-t pt-6">
              <h3 className="mb-4 flex items-center gap-2 text-base font-semibold">
                <MapPin size={18} />
                Structured Address
              </h3>
              <div className="space-y-1 rounded-lg border bg-white p-4">
                {data.registeredAddress?.streetAddress && (
                  <div className="text-sm text-slate-700">
                    {data.registeredAddress.streetAddress}
                  </div>
                )}
                <div className="text-sm text-slate-700">
                  {[
                    data.registeredAddress?.locality,
                    data.registeredAddress?.region,
                    data.registeredAddress?.postalCode,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </div>
                {data.registeredAddress?.country && (
                  <div className="text-sm text-slate-700">{data.registeredAddress.country}</div>
                )}
              </div>
            </div>
          )}

          {hasOfficerDetails && (
            <div className="border-t pt-6">
              <h3 className="mb-4 flex items-center gap-2 text-base font-semibold">
                <Users size={18} />
                Officer Details
              </h3>
              <div className="space-y-3">
                {officersWithDetails.map((officer, idx) => (
                  <div key={idx} className="rounded-lg border bg-white p-4">
                    <div className="mb-2 text-sm font-semibold">{officer.name}</div>
                    {officer.occupation && (
                      <div className="mb-1 text-xs text-slate-600">
                        Occupation: {officer.occupation}
                      </div>
                    )}
                    {officer.currentStatus && (
                      <div className="text-xs text-slate-600">Status: {officer.currentStatus}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
